"use client";

import { CheckIcon, ChevronDownIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../../lib";
import { Button } from "../button";
import { ToggleSwitch } from "../custom/toggle-switch";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from "../dropdown-menu";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../select";
import {
	type FieldLabelVariant,
	type FieldLayoutHint,
	FieldShell,
} from "./field-shell";

export type SingleSelectPresentation =
	| "tiles"
	| "dropdown"
	| "radio"
	| "segmented"
	| "check_menu";

export interface SingleSelectOption {
	value: string;
	label: string;
	description?: string;
	imageSrc?: string;
	icon?: ReactNode;
	/** Optional trailing hint when not selected (check_menu). */
	trailingHint?: string | null;
	disabled?: boolean;
}

export interface SingleSelectFieldProps {
	id: string;
	label: string;
	value: string | null;
	options: SingleSelectOption[];
	onChange: (value: string) => void;
	description?: string;
	labelInfo?: ReactNode;
	labelInfoAriaLabel?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	name?: string;
	onBlur?: () => void;
	layoutHint?: FieldLayoutHint;
	presentation?: SingleSelectPresentation;
	mixed?: boolean;
	mixedTooltip?: ReactNode;
	/** Caller-resolved string shown when mixed and no display value. */
	mixedPlaceholder?: string;
	placeholder?: string;
	/** When mixed, optional explicit display override (parity with ScopeTileSelector). */
	displayValue?: string | null;
	collapsed?: boolean;
	collapsedAction?: ReactNode;
	tileVariant?: "compact" | "large" | "settings";
	className?: string;
	disabled?: boolean;
	/** Hide FieldShell label (check_menu / toolbar segmented chrome). */
	hideLabel?: boolean;
	labelVariant?: FieldLabelVariant;
	labelTrailing?: ReactNode;
	/** Segmented presentation: icon buttons vs text labels (default text). */
	segmentedOptionDisplay?: "icon" | "text";
	/** Segmented presentation: ToggleSwitch layoutId (defaults to `${id}-select`). */
	layoutId?: string;
	/** Segmented presentation: track size (default `default`). */
	size?: "default" | "compact";
	/** Segmented presentation: disable layoutId pill animation during parent drag. */
	layoutAnimationsEnabled?: boolean;
	/** Segmented presentation: accent track when right-most option is active. */
	highlightWhenRightActive?: boolean;
	/** Override check_menu trigger contents (icons + label). */
	triggerContent?: ReactNode;
	triggerClassName?: string;
	/** check_menu group label inside the dropdown. */
	menuLabel?: string;
	menuClassName?: string;
	testId?: string;
	/** Prefix for option test ids (`${prefix}-option-${value}`). Defaults to testId. */
	optionTestIdPrefix?: string;
	/**
	 * Dropdown only: size the trigger to the longest option label (invisible
	 * sizer), matching entity-viewer / TabsListOrDropdown chrome. Caps at
	 * `max-w-full` so narrow parents still truncate.
	 */
	sizeToLongestOption?: boolean;
}

export function SingleSelectField({
	id,
	label,
	value,
	options,
	onChange,
	description,
	labelInfo,
	labelInfoAriaLabel,
	error,
	reserveErrorSpace = false,
	name,
	onBlur,
	layoutHint,
	presentation = "tiles",
	mixed = false,
	mixedTooltip,
	mixedPlaceholder = "Mixed values",
	placeholder = "Select…",
	displayValue,
	collapsed = false,
	collapsedAction,
	tileVariant = "settings",
	className,
	disabled = false,
	hideLabel = false,
	labelVariant = "compact",
	labelTrailing,
	triggerContent,
	triggerClassName,
	menuLabel,
	menuClassName,
	testId,
	optionTestIdPrefix,
	sizeToLongestOption = false,
	segmentedOptionDisplay = "text",
	layoutId,
	size = "default",
	layoutAnimationsEnabled = true,
	highlightWhenRightActive = false,
}: SingleSelectFieldProps) {
	const mixedId = `${id}-mixed`;
	const controlInvalid = error ? true : undefined;
	const effectiveValue = displayValue ?? value;
	const showSelection = !mixed || displayValue != null;
	const selectedLabel = options.find(
		(o) => o.value === effectiveValue,
	)?.label;
	const longestOptionLabel =
		sizeToLongestOption && options.length > 0
			? options.reduce(
					(longest, option) =>
						option.label.length >= longest.length
							? option.label
							: longest,
					options[0]?.label ?? "",
				)
			: null;

	if (collapsed) {
		return (
			<div
				className={cn(
					"@container/field rounded-xl border bg-card px-4 py-3 text-sm",
					className,
				)}
			>
				<div className="flex items-center justify-between gap-3">
					<div className="min-w-0 flex-1">
						<div className="flex flex-col gap-1 @sm:flex-row @sm:items-center @sm:justify-between @sm:gap-2">
							<span className="text-muted-foreground">
								{label}
							</span>
							<span className="shrink-0 self-start rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-foreground @sm:self-auto">
								{mixed && displayValue == null
									? mixedPlaceholder
									: (selectedLabel ?? "Not set")}
							</span>
						</div>
					</div>
					{collapsedAction}
				</div>
			</div>
		);
	}

	if (presentation === "check_menu") {
		const menu = (
			<>
				{name ? (
					<input
						type="hidden"
						name={name}
						value={effectiveValue ?? ""}
						readOnly
					/>
				) : null}
				{mixed ? (
					<p
						id={mixedId}
						className="mb-2 text-xs text-muted-foreground"
					>
						{mixedTooltip ?? mixedPlaceholder}
					</p>
				) : null}
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							type="button"
							id={id}
							variant="ghost"
							size="sm"
							disabled={disabled}
							data-testid={testId}
							aria-label={label}
							aria-invalid={controlInvalid}
							onBlur={onBlur}
							className={cn(
								"h-8 w-fit shrink-0 gap-1.5 px-2 text-muted-foreground hover:text-foreground data-[state=open]:bg-accent [&>svg]:mr-0 [&>svg+svg]:block",
								triggerClassName,
							)}
						>
							{triggerContent ?? (
								<>
									<span className="truncate">
										{selectedLabel ?? placeholder}
									</span>
									<ChevronDownIcon className="size-3.5 shrink-0 opacity-50" />
								</>
							)}
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						align="start"
						className={cn("w-[280px]", menuClassName)}
					>
						{menuLabel ? (
							<DropdownMenuLabel>{menuLabel}</DropdownMenuLabel>
						) : null}
						{options.map((option) => {
							const selected =
								showSelection &&
								option.value === effectiveValue;
							const optionPrefix = optionTestIdPrefix ?? testId;
							return (
								<DropdownMenuItem
									key={option.value}
									className="flex cursor-pointer items-center gap-2"
									data-testid={
										optionPrefix
											? `${optionPrefix}-option-${option.value}`
											: undefined
									}
									onSelect={() => onChange(option.value)}
								>
									{option.icon}
									<div className="min-w-0 flex-1">
										<div className="font-medium text-sm">
											{option.label}
										</div>
										{option.description ? (
											<div className="text-muted-foreground text-xs">
												{option.description}
											</div>
										) : null}
									</div>
									{selected ? (
										<CheckIcon className="ml-auto size-3.5 shrink-0 text-primary" />
									) : option.trailingHint ? (
										<span className="ml-auto shrink-0 text-xs text-muted-foreground">
											{option.trailingHint}
										</span>
									) : null}
								</DropdownMenuItem>
							);
						})}
					</DropdownMenuContent>
				</DropdownMenu>
			</>
		);

		// Toolbar / chrome pickers (hideLabel): skip FieldShell — its default
		// `w-full` expands over sibling split controls in flex toolbars.
		if (hideLabel && !description && !error && !reserveErrorSpace) {
			return (
				<div
					className={cn("inline-flex w-fit shrink-0", className)}
					data-field-shell="inline"
				>
					{menu}
				</div>
			);
		}

		return (
			<FieldShell
				id={id}
				label={hideLabel ? undefined : label}
				description={description}
				labelInfo={labelInfo}
				labelInfoAriaLabel={labelInfoAriaLabel}
				error={error}
				reserveErrorSpace={reserveErrorSpace}
				layoutHint={layoutHint}
				className={cn("w-fit", className)}
				describedById={mixed ? mixedId : undefined}
				labelVariant={labelVariant}
				labelTrailing={labelTrailing}
			>
				{menu}
			</FieldShell>
		);
	}

	if (presentation === "dropdown") {
		const isToolbarIntrinsic =
			sizeToLongestOption ||
			(hideLabel && !description && !error && !reserveErrorSpace);
		const dropdownControl = (
			<>
				{name ? (
					<input
						type="hidden"
						name={name}
						value={effectiveValue ?? ""}
						readOnly
					/>
				) : null}
				{mixed ? (
					<p
						id={mixedId}
						className="mb-2 text-xs text-muted-foreground"
					>
						{mixedTooltip ?? mixedPlaceholder}
					</p>
				) : null}
				<Select
					value={
						showSelection && effectiveValue
							? effectiveValue
							: undefined
					}
					onValueChange={onChange}
					disabled={disabled}
				>
					<SelectTrigger
						id={id}
						aria-label={label}
						aria-invalid={controlInvalid}
						className={cn(
							sizeToLongestOption
								? "w-max max-w-full shrink-0 justify-between gap-2"
								: isToolbarIntrinsic
									? "w-max max-w-full shrink-0"
									: undefined,
							triggerClassName,
						)}
						data-testid={testId}
						onBlur={onBlur}
					>
						{sizeToLongestOption ? (
							<span className="inline-grid max-w-full shrink-0">
								<span
									className="col-start-1 row-start-1 select-none whitespace-nowrap opacity-0"
									aria-hidden
								>
									{longestOptionLabel || "—"}
								</span>
								<span className="col-start-1 row-start-1 min-w-0 truncate">
									<SelectValue
										placeholder={
											mixed
												? mixedPlaceholder
												: placeholder
										}
									/>
								</span>
							</span>
						) : (
							<span className="min-w-0 flex-1 truncate text-left">
								<SelectValue
									placeholder={
										mixed ? mixedPlaceholder : placeholder
									}
								/>
							</span>
						)}
					</SelectTrigger>
					<SelectContent
						className={
							sizeToLongestOption
								? "min-w-[var(--radix-select-trigger-width)] w-max max-w-[calc(100vw-2rem)]"
								: undefined
						}
					>
						{options.map((option) => (
							<SelectItem
								key={option.value}
								value={option.value}
								disabled={option.disabled}
								className={
									sizeToLongestOption
										? "whitespace-nowrap"
										: undefined
								}
							>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</>
		);

		// Toolbar / chrome pickers: skip FieldShell `w-full` so siblings
		// (ellipsis menus, icons) are not crushed in flex/grid chrome.
		if (isToolbarIntrinsic) {
			return (
				<div
					className={cn(
						"inline-flex w-max max-w-full shrink-0 flex-col",
						className,
					)}
					data-field-shell="inline"
					data-width-mode="intrinsic"
				>
					{dropdownControl}
				</div>
			);
		}

		return (
			<FieldShell
				id={id}
				label={hideLabel ? undefined : label}
				description={description}
				labelInfo={labelInfo}
				labelInfoAriaLabel={labelInfoAriaLabel}
				error={error}
				reserveErrorSpace={reserveErrorSpace}
				layoutHint={layoutHint}
				widthMode={sizeToLongestOption ? "intrinsic" : "full"}
				className={className}
				describedById={mixed ? mixedId : undefined}
				labelVariant={labelVariant}
				labelTrailing={labelTrailing}
			>
				{dropdownControl}
			</FieldShell>
		);
	}

	if (presentation === "segmented") {
		const segmentedOptions = options.map((option) => {
			if (segmentedOptionDisplay === "icon" && option.icon) {
				return {
					id: option.value,
					ariaLabel: option.label,
					icon: option.icon,
				};
			}
			return {
				id: option.value,
				ariaLabel: option.label,
				label: option.label,
			};
		});
		const fallbackId = options[0]?.value ?? "";
		const segmentedControl =
			segmentedOptions.length >= 2 ? (
				<ToggleSwitch
					layoutId={layoutId ?? `${id}-select`}
					value={(effectiveValue as string) || fallbackId}
					mixed={mixed && displayValue == null}
					disabled={disabled}
					size={size}
					layoutAnimationsEnabled={layoutAnimationsEnabled}
					highlightWhenRightActive={highlightWhenRightActive}
					optionDisplay={segmentedOptionDisplay}
					aria-invalid={controlInvalid}
					onValueChange={onChange}
					options={
						segmentedOptions as [
							{ id: string; ariaLabel: string; label: string },
							{ id: string; ariaLabel: string; label: string },
							...{
								id: string;
								ariaLabel: string;
								label: string;
							}[],
						]
					}
				/>
			) : null;

		if (hideLabel) {
			return (
				<div className={className} data-testid={testId} onBlur={onBlur}>
					{name ? (
						<input
							type="hidden"
							name={name}
							value={effectiveValue ?? ""}
							readOnly
						/>
					) : null}
					{mixed ? (
						<p
							id={mixedId}
							className="mb-2 text-xs text-muted-foreground"
						>
							{mixedTooltip ?? mixedPlaceholder}
						</p>
					) : null}
					{segmentedControl}
				</div>
			);
		}

		return (
			<FieldShell
				id={id}
				label={label}
				description={description}
				labelInfo={labelInfo}
				labelInfoAriaLabel={labelInfoAriaLabel}
				error={error}
				reserveErrorSpace={reserveErrorSpace}
				layoutHint={layoutHint}
				className={className}
				describedById={mixed ? mixedId : undefined}
				labelVariant={labelVariant}
				labelTrailing={labelTrailing}
			>
				{name ? (
					<input
						type="hidden"
						name={name}
						value={effectiveValue ?? ""}
						readOnly
					/>
				) : null}
				{mixed ? (
					<p
						id={mixedId}
						className="mb-2 text-xs text-muted-foreground"
					>
						{mixedTooltip ?? mixedPlaceholder}
					</p>
				) : null}
				<div onBlur={onBlur}>{segmentedControl}</div>
			</FieldShell>
		);
	}

	if (presentation === "radio") {
		return (
			<FieldShell
				id={id}
				label={hideLabel ? undefined : label}
				description={description}
				labelInfo={labelInfo}
				labelInfoAriaLabel={labelInfoAriaLabel}
				error={error}
				reserveErrorSpace={reserveErrorSpace}
				layoutHint={layoutHint}
				className={className}
				describedById={mixed ? mixedId : undefined}
				labelVariant={labelVariant}
				labelTrailing={labelTrailing}
			>
				{mixed ? (
					<p
						id={mixedId}
						className="mb-2 text-xs text-muted-foreground"
					>
						{mixedTooltip ?? mixedPlaceholder}
					</p>
				) : null}
				<div
					role="radiogroup"
					aria-label={label}
					aria-invalid={controlInvalid}
					onBlur={onBlur}
					className="flex flex-col gap-2"
				>
					{options.map((option) => {
						const selected =
							showSelection && option.value === effectiveValue;
						return (
							<label
								key={option.value}
								className="flex cursor-pointer items-center gap-2 text-sm"
							>
								<input
									type="radio"
									name={name ?? id}
									value={option.value}
									checked={selected}
									disabled={disabled}
									onChange={() => onChange(option.value)}
								/>
								{option.label}
							</label>
						);
					})}
				</div>
			</FieldShell>
		);
	}

	// tiles (default) — SettingsSelector + ScopeTileSelector parity
	const isSettingsTiles = tileVariant === "settings";
	return (
		<FieldShell
			id={id}
			label={isSettingsTiles ? undefined : label}
			description={description}
			labelInfo={labelInfo}
			labelInfoAriaLabel={labelInfoAriaLabel}
			error={error}
			reserveErrorSpace={reserveErrorSpace}
			layoutHint={layoutHint}
			className={cn(
				isSettingsTiles && "rounded-xl border bg-card p-4 text-sm",
				className,
			)}
			describedById={mixed ? mixedId : undefined}
			labelVariant={labelVariant}
			labelTrailing={labelTrailing}
		>
			{name ? (
				<input
					type="hidden"
					name={name}
					value={effectiveValue ?? ""}
					readOnly
				/>
			) : null}
			{isSettingsTiles ? (
				<>
					<div className="mb-1 font-medium text-card-foreground">
						{label}
					</div>
				</>
			) : null}
			{mixed && mixedTooltip ? (
				<div className="mb-2" id={mixedId}>
					{mixedTooltip}
				</div>
			) : null}
			<div
				id={id}
				role="radiogroup"
				aria-label={label}
				aria-invalid={controlInvalid}
				aria-describedby={mixed ? mixedId : undefined}
				onBlur={onBlur}
				className={cn(
					"grid gap-2",
					tileVariant === "large"
						? "grid-cols-2 gap-3"
						: tileVariant === "compact"
							? "grid-cols-2 gap-1.5 sm:grid-cols-3"
							: "sm:grid-cols-2",
				)}
			>
				{options.map((option) => {
					const selected =
						showSelection && option.value === effectiveValue;
					return (
						<button
							key={option.value}
							type="button"
							role="radio"
							aria-checked={selected}
							disabled={disabled}
							onClick={() => onChange(option.value)}
							className={cn(
								"relative flex cursor-pointer flex-col items-start rounded-lg border text-left transition-colors",
								tileVariant === "large"
									? "min-h-[5.5rem] px-3 py-3 text-xs"
									: tileVariant === "compact"
										? "px-2 py-1.5 text-[11px]"
										: "p-3",
								selected
									? "border-primary bg-primary/5 ring-1 ring-primary"
									: "border-border hover:border-primary/50 hover:bg-muted/50",
							)}
						>
							{selected ? (
								<CheckIcon
									className="absolute top-2 right-2 size-4 text-primary"
									aria-hidden
								/>
							) : null}
							{option.imageSrc ? (
								<div className="mb-2 flex justify-center self-stretch">
									<img
										src={option.imageSrc}
										alt=""
										className="h-10 w-10 object-contain"
									/>
								</div>
							) : null}
							<span className="font-medium text-foreground">
								{option.label}
							</span>
							{option.description ? (
								<span className="mt-0.5 text-xs text-muted-foreground">
									{option.description}
								</span>
							) : null}
						</button>
					);
				})}
			</div>
			{mixed && displayValue == null ? (
				<p className="mt-2 text-[11px] text-muted-foreground">
					{mixedPlaceholder}
				</p>
			) : null}
			{isSettingsTiles && !mixed ? (
				<div className="mt-3 text-xs text-muted-foreground">
					{effectiveValue ? (
						<>
							Selected:{" "}
							<span className="font-medium text-foreground">
								{selectedLabel}
							</span>
						</>
					) : (
						"Please select an option"
					)}
				</div>
			) : null}
		</FieldShell>
	);
}
